import React, { useState } from "react";
import CalculatorPage from "./CalculatorPage";

function BMICalculator() {
  const [weight, setWeight] = useState("");
  const [height, setHeight] = useState("");
  const [bmi, setBmi] = useState(null);
  const [category, setCategory] = useState("");
  const [error, setError] = useState("");
  const [funFact, setFunFact] = useState("");
  const [showFunFact, setShowFunFact] = useState(false);

  const onInput = (setter) => (e) => {
    setter(e.target.value);
    setBmi(null);
    setShowFunFact(false);
    setError("");
  };

  const calculateBMI = () => {
    const w = parseFloat(weight);
    const h = parseFloat(height);
    if (isNaN(w) || isNaN(h) || w <= 0 || h <= 0) {
      setError("Please enter a valid weight and height.");
      setBmi(null);
      setShowFunFact(false);
      return;
    }

    const meters = h / 100;
    const bmiValue = (w / (meters * meters)).toFixed(1);
    setError("");
    setBmi(bmiValue);

    if (bmiValue < 18.5) setCategory("Underweight");
    else if (bmiValue < 25) setCategory("Normal weight");
    else if (bmiValue < 30) setCategory("Overweight");
    else setCategory("Obese");

    const funFactsList = [
      "BMI is a quick measure but does not account for muscle mass.",
      "The concept of BMI was introduced in the 1830s by Lambert Adolphe Quetelet.",
      "A healthy BMI is generally between 18.5 and 24.9.",
      "Athletes often have higher BMI due to more muscle mass.",
      "BMI is used worldwide for quick health screening.",
    ];

    setFunFact(funFactsList[Math.floor(Math.random() * funFactsList.length)]);
    setShowFunFact(true);
  };

  return (
    <CalculatorPage
      title="BMI Calculator"
      metaDescription="Free BMI calculator: enter your weight and height to get your Body Mass Index and see whether you're in the underweight, normal, overweight, or obese range."
      intro={
        <>
          The <strong>BMI calculator</strong> helps you know if you're in a
          healthy weight range for your height.
        </>
      }
      seoContent={
        <>
          <h2>How BMI is calculated</h2>
          <p>
            Body Mass Index is your weight in kilograms divided by your height
            in meters squared: BMI = kg / m². The World Health Organization
            ranges are: under 18.5 underweight, 18.5–24.9 normal, 25–29.9
            overweight, and 30 or above obese.
          </p>
          <h2>What BMI can and can't tell you</h2>
          <p>
            BMI is a fast screening tool, not a diagnosis. It doesn't
            distinguish muscle from fat, so muscular athletes can register as
            "overweight" while being very fit, and it doesn't account for age,
            sex, or body composition. Use it as a starting point, and talk to
            a healthcare professional before making decisions based on it.
          </p>
          <h2>Frequently asked questions</h2>
          <h3>What is a healthy BMI range?</h3>
          <p>
            For most adults a BMI between 18.5 and 24.9 is considered the
            healthy range. Below 18.5 is classed as underweight and 25 or above
            as overweight, but these are population averages rather than
            individual targets.
          </p>
          <h3>Is BMI accurate for athletes?</h3>
          <p>
            Not always. Muscle is denser than fat, so a very muscular person
            can have a high BMI while carrying little body fat. For athletes,
            measures like body-fat percentage or waist circumference give a
            clearer picture.
          </p>
          <h3>Should I use a different BMI chart for children?</h3>
          <p>
            Yes. Children and teenagers are assessed with age- and sex-specific
            BMI-for-age percentiles rather than the fixed adult ranges, so this
            adult calculator isn't suitable for under-18s.
          </p>
        </>
      }
    >
      <div className="fields">
        <label className="field">
          <span>Weight (kg)</span>
          <input type="number" value={weight} onChange={onInput(setWeight)} placeholder="e.g. 70" />
        </label>
        <label className="field">
          <span>Height (cm)</span>
          <input type="number" value={height} onChange={onInput(setHeight)} placeholder="e.g. 175" />
        </label>
      </div>

      <button className="btn" onClick={calculateBMI}>Calculate BMI</button>

      {error && <div className="error">{error}</div>}

      {bmi && (
        <>
          <div className="result">
            Your BMI is <strong>{bmi}</strong> ({category})
          </div>
          {showFunFact && (
            <div className="fun-fact fun-fact-animate">💡 {funFact}</div>
          )}
        </>
      )}
    </CalculatorPage>
  );
}

export default BMICalculator;
